import { type CSSProperties } from "react";
import type { WizardController } from "../../../hooks/use-wrapped-api-wizard";
import type { WrappedApiAuthKind } from "../../../hooks/use-wrapped-apis";
import { WizardField, wizardStyles, ConfidenceBadge } from "./wizard-ui";

const AUTH_KINDS: WrappedApiAuthKind[] = ["bearer", "api_key", "basic", "oauth2_auth_code", "oauth2_client_credentials"];

function authKindLabel(k: WrappedApiAuthKind): string {
  switch (k) {
    case "api_key": return "API key";
    case "oauth2_auth_code": return "OAuth (auth code)";
    case "oauth2_client_credentials": return "OAuth (client creds)";
    default: return k.charAt(0).toUpperCase() + k.slice(1);
  }
}

/**
 * Step 2 — Identify. Confirm slug + name + auth kind (AI-proposed, editable). For
 * OAuth, well-known discovery (run in Step 1) pre-fills the authorize/token URLs +
 * scopes; the owner adds only client_id + secret. "Continue" creates the wrapper.
 */
export function Step2Identify({ wizard }: { wizard: WizardController }) {
  const p = wizard.proposal;
  const setOauth = (patch: Partial<typeof wizard.oauth>) => wizard.setOauth({ ...wizard.oauth, ...patch });
  const disc = p?.oauth_discovery;

  return (
    <div>
      <p style={wizardStyles.intro}>
        Confirm the basics. Everything here is an AI proposal you can edit. Continue creates the wrapper so the next
        steps can test against it live.
      </p>

      <div style={{ display: "flex", gap: 12 }}>
        <WizardField label="Name">
          <input value={wizard.name} onChange={(e) => wizard.setName(e.target.value)} style={wizardStyles.input} />
        </WizardField>
        <WizardField label="Slug" hint="Tool prefix + /mcp/w/<slug> endpoint.">
          <input
            value={wizard.slug}
            onChange={(e) => wizard.setSlug(e.target.value.toLowerCase())}
            style={{ ...wizardStyles.input, fontFamily: "var(--font-mono)" }}
          />
        </WizardField>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={wizardStyles.label}>Authentication</span>
          {p && <ConfidenceBadge confidence={p.auth_confidence} />}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {AUTH_KINDS.map((k) => (
            <button key={k} type="button" onClick={() => wizard.setAuthKind(k)} style={authChip(wizard.authKind === k)}>
              {authKindLabel(k)}
            </button>
          ))}
        </div>
        {p && p.auth_confidence === "low" && (
          <div style={{ ...wizardStyles.hint, marginTop: 6 }}>
            No auth scheme was declared in the spec — defaulted to Bearer. Double-check the provider's docs.
          </div>
        )}
      </div>

      {wizard.isOAuth && (
        <div style={oauthBox}>
          {disc?.discovered ? (
            <div style={discoveredNote}>
              <i className="ti ti-circle-check" style={{ fontSize: 13 }} aria-hidden="true" /> Auto-discovered OAuth
              endpoints from <code style={{ fontFamily: "var(--font-mono)" }}>{disc.source}</code> — just add your client credentials.
            </div>
          ) : (
            <div style={{ ...wizardStyles.hint, marginBottom: 8 }}>
              Couldn't auto-discover OAuth config — enter the endpoints manually.
            </div>
          )}
          <WizardField label="Client ID">
            <input value={wizard.oauth.clientId} onChange={(e) => setOauth({ clientId: e.target.value })} style={wizardStyles.input} autoComplete="off" />
          </WizardField>
          <WizardField label="Client secret">
            <input type="password" value={wizard.oauth.clientSecret} onChange={(e) => setOauth({ clientSecret: e.target.value })} style={wizardStyles.input} autoComplete="off" />
          </WizardField>
          {wizard.authKind === "oauth2_auth_code" && (
            <WizardField label="Authorize URL">
              <input value={wizard.oauth.authorizeUrl} onChange={(e) => setOauth({ authorizeUrl: e.target.value })} placeholder="https://provider/oauth/authorize" style={wizardStyles.input} />
            </WizardField>
          )}
          <WizardField label="Token URL">
            <input value={wizard.oauth.tokenUrl} onChange={(e) => setOauth({ tokenUrl: e.target.value })} placeholder="https://provider/oauth/token" style={wizardStyles.input} />
          </WizardField>
          <WizardField label="Scopes (optional)">
            <input value={wizard.oauth.scopes} onChange={(e) => setOauth({ scopes: e.target.value })} placeholder="read write" style={wizardStyles.input} />
          </WizardField>
        </div>
      )}

      <details>
        <summary style={{ fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer", marginBottom: 8 }}>
          Advanced
        </summary>
        <WizardField label="Base URL override" hint="Defaults to the spec's first server.">
          <input value={wizard.baseUrl} onChange={(e) => wizard.setBaseUrl(e.target.value)} placeholder="https://api.example.com" style={wizardStyles.input} />
        </WizardField>
      </details>
    </div>
  );
}

const oauthBox: CSSProperties = {
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "10px 12px",
  marginBottom: 12,
  background: "var(--color-background-secondary)",
};
const discoveredNote: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 11,
  color: "var(--c-green-800, var(--color-text-secondary))",
  marginBottom: 10,
  lineHeight: 1.5,
};

function authChip(on: boolean): CSSProperties {
  return {
    fontSize: 12,
    padding: "5px 12px",
    borderRadius: "var(--border-radius-sm)",
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    background: on ? "var(--c-blue-50)" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: on ? 500 : 400,
  };
}
